import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Button,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useReclassifyCore } from '../../../../core/modules/reclassify';
import { useLocationCore } from '../../../../core/modules/location';
import { useContainerCore } from '../../../../core/modules/container';
import { useCompanyCore } from '../../../../core/modules/company';
import { DestinationTypeSelector, DestinationType } from '../../shared/DestinationTypeSelector';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { useMasterCore } from '../../../../core/modules/master';

export const ReclassifyFilters: React.FC = () => {
  const { filters, loadArticles, setFilters, getProviderAreas, productStates } =
    useReclassifyCore();
  
  const { locations, getLocations } = useLocationCore();
  const { containers, getContainers } = useContainerCore();
  const { companyId } = useCompanyCore();
  const { warehouses, getWarehouses } = useWarehouseCore();
  const { containerCategories, containerTypes, channelTypes, getCategories, getTypes } = useMasterCore();

  const [localFilters, setLocalFilters] = useState({
    // Filtros primarios (mutuamente excluyentes)
    location_id: undefined as number | undefined,
    container_id: undefined as number | undefined,
    
    // Filtros secundarios
    origin_container_code: '', // Código del contenedor origen
    code: '',                  // Código del producto
    description: '',           // Descripción del producto
    reference: '',             // Referencia
    status: 0 as number,       // Estado del producto
    provider_id: undefined as number | undefined, // ID del proveedor
  });

  const [destinationType, setDestinationType] = useState<DestinationType>('location');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [quickSearch, setQuickSearch] = useState({
    code: '',
    description: '',
    status: 0 as number,
  });

  // Obtener áreas de proveedores
  const providerAreas = getProviderAreas();

  useEffect(() => {
    // Cargar datos necesarios
    if (companyId) {
      getLocations(companyId);
      getContainers(companyId, {});
      getWarehouses(companyId);
      getCategories(companyId, 'container');
      getTypes(companyId, 'container');
      getTypes(companyId, 'channel');
    }
    // NO cargar artículos inicialmente - solo cuando se seleccione un filtro primario
  }, [getLocations, getContainers, getWarehouses, getCategories, getTypes, companyId]);

  // Manejar cambio de tipo de destino
  const handleDestinationTypeChange = (type: DestinationType) => {
    setDestinationType(type);
    // Limpiar filtros cuando se cambia el tipo
    const newFilters = {
      ...localFilters,
      location_id: undefined,
      container_id: undefined,
    };
    setLocalFilters(newFilters);
    setFilters({ page: 1 });
    loadArticles({ page: 1 });
  };

  // Filtros primarios (mutuamente excluyentes)
  const handleLocationChange = (locationId: number | null) => {
    setDestinationType('location');
    // Limpiar todos los filtros secundarios cuando se selecciona un origen primario
    const newFilters = { 
      location_id: locationId || undefined,
      container_id: undefined,
      // Limpiar filtros secundarios
      origin_container_code: '',
      code: '',
      description: '',
      reference: '',
      status: 0,
      provider_id: undefined,
    };
    setLocalFilters(newFilters);
    setQuickSearch({ code: '', description: '', status: 0 });

    const searchFilters = {
      ...newFilters,
      page: 1,
    };
    // Limpiar filtros undefined y vacíos
    Object.keys(searchFilters).forEach(key => {
      const value = searchFilters[key as keyof typeof searchFilters];
      if (value === undefined || value === '' || value === 0) {
        delete searchFilters[key as keyof typeof searchFilters];
      }
    });
    
    setFilters(searchFilters);
    loadArticles(searchFilters);
  };

  const handleContainerChange = (containerId: number | null) => {
    setDestinationType('container');
    // Limpiar todos los filtros secundarios cuando se selecciona un origen primario
    const newFilters = { 
      container_id: containerId || undefined,
      location_id: undefined,
      // Limpiar filtros secundarios
      origin_container_code: '',
      code: '',
      description: '',
      reference: '',
      status: 0,
      provider_id: undefined,
    };
    setLocalFilters(newFilters);
    setQuickSearch({ code: '', description: '', status: 0 });

    const searchFilters = {
      ...newFilters,
      page: 1,
    };
    // Limpiar filtros undefined y vacíos
    Object.keys(searchFilters).forEach(key => {
      const value = searchFilters[key as keyof typeof searchFilters];
      if (value === undefined || value === '' || value === 0) {
        delete searchFilters[key as keyof typeof searchFilters];
      }
    });
    
    setFilters(searchFilters);
    loadArticles(searchFilters);
  };

  // Filtros secundarios
  const handleContainerCodeChange = (value: string) => {
    const newFilters = { 
      ...localFilters, 
      origin_container_code: value
    };
    setLocalFilters(newFilters);

    // Debounce para la búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const searchFilters = { ...newFilters, page: 1 };
      // Limpiar filtros undefined y vacíos
      Object.keys(searchFilters).forEach(key => {
        const value = searchFilters[key as keyof typeof searchFilters];
        if (value === undefined || value === '') {
          delete searchFilters[key as keyof typeof searchFilters];
        }
      });
      
      console.log('Filtros enviados:', searchFilters);
      setFilters(searchFilters);
      loadArticles(searchFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleCodeChange = (value: string) => {
    const newFilters = { ...localFilters, code: value };
    setLocalFilters(newFilters);

    // Debounce para la búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const searchFilters = { ...newFilters, page: 1 };
      // Limpiar filtros undefined y vacíos
      Object.keys(searchFilters).forEach(key => {
        const value = searchFilters[key as keyof typeof searchFilters];
        if (value === undefined || value === '') {
          delete searchFilters[key as keyof typeof searchFilters];
        }
      });
      setFilters(searchFilters);
      loadArticles(searchFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleDescriptionChange = (value: string) => {
    const newFilters = { ...localFilters, description: value };
    setLocalFilters(newFilters);

    // Debounce para la búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const searchFilters = { ...newFilters, page: 1 };
      // Limpiar filtros undefined y vacíos
      Object.keys(searchFilters).forEach(key => {
        const value = searchFilters[key as keyof typeof searchFilters];
        if (value === undefined || value === '') {
          delete searchFilters[key as keyof typeof searchFilters];
        }
      });
      setFilters(searchFilters);
      loadArticles(searchFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleReferenceChange = (value: string) => {
    const newFilters = { ...localFilters, reference: value };
    setLocalFilters(newFilters);

    // Debounce para la búsqueda
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const searchFilters = { ...newFilters, page: 1 };
      // Limpiar filtros undefined y vacíos
      Object.keys(searchFilters).forEach(key => {
        const value = searchFilters[key as keyof typeof searchFilters];
        if (value === undefined || value === '') {
          delete searchFilters[key as keyof typeof searchFilters];
        }
      });
      setFilters(searchFilters);
      loadArticles(searchFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleProviderAreaChange = (areaId: number | null) => {
    const newFilters = { ...localFilters, provider_id: areaId || undefined };
    setLocalFilters(newFilters);

    const searchFilters = { ...newFilters, page: 1 };
    // Limpiar filtros undefined y vacíos
    Object.keys(searchFilters).forEach(key => {
      const value = searchFilters[key as keyof typeof searchFilters];
      if (value === undefined || value === '') {
        delete searchFilters[key as keyof typeof searchFilters];
      }
    });
    
    setFilters(searchFilters);
    loadArticles(searchFilters);
  };

  const handleClearFilters = () => {
    setDestinationType('location');
    setLocalFilters({
      location_id: undefined,
      container_id: undefined,
      origin_container_code: '',
      code: '',
      description: '',
      reference: '',
      status: 0,
      provider_id: undefined,
    });
    setQuickSearch({ code: '', description: '', status: 0 });
    setFilters({ page: 1 });
    loadArticles({ page: 1 });
  };

  // Búsqueda rápida
  const handleQuickSearchChange = (field: string, value: string | number) => {
    setQuickSearch(prev => ({ ...prev, [field]: value }));
    
    // Aplicar búsqueda con debounce
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      const newFilters = {
        ...localFilters,
        code: field === 'code' ? value as string : localFilters.code,
        description: field === 'description' ? value as string : localFilters.description,
        // Para el estado, usamos el ID del estado
        status: field === 'status' ? (value as number) : localFilters.status,
        page: 1,
      };
      
      // Limpiar filtros undefined y vacíos
      Object.keys(newFilters).forEach(key => {
        const val = newFilters[key as keyof typeof newFilters];
        if (val === undefined || val === '' || val === 0) {
          delete newFilters[key as keyof typeof newFilters];
        }
      });
      
      setFilters(newFilters);
      loadArticles(newFilters);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleQuickSearchSubmit = () => {
    const newFilters = {
      ...localFilters,
      code: quickSearch.code,
      description: quickSearch.description,
      status: quickSearch.status,
      page: 1,
    };
    
    // Limpiar filtros undefined y vacíos
    Object.keys(newFilters).forEach(key => {
      const val = newFilters[key as keyof typeof newFilters];
      if (val === undefined || val === '' || val === 0) {
        delete newFilters[key as keyof typeof newFilters];
      }
    });
    
    setFilters(newFilters);
    loadArticles(newFilters);
  };

  const hasActiveFilters = 
    localFilters.location_id ||
    localFilters.container_id ||
    localFilters.origin_container_code ||
    localFilters.code || 
    localFilters.description || 
    localFilters.reference ||
    localFilters.provider_id ||
    quickSearch.code ||
    quickSearch.description ||
    quickSearch.status > 0;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterIcon color="action" />
          <Typography variant="h6">Filtros de búsqueda</Typography>
          {hasActiveFilters && (
            <Tooltip title="Limpiar filtros">
              <IconButton size="small" onClick={handleClearFilters}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Filtros primarios */}

        
        <Box mb={3}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box flex={1} mr={2}>
              <DestinationTypeSelector
                destinationType={destinationType}
                onDestinationTypeChange={handleDestinationTypeChange}
                selectedContainer={localFilters.container_id || null}
                onContainerChange={handleContainerChange}
                selectedLocation={localFilters.location_id || null}
                onLocationChange={handleLocationChange}
                containers={containers || []}
                locations={locations || []}
                containerCategories={containerCategories || []}
                containerTypes={containerTypes || []}
                warehouses={warehouses || []}
                channelTypes={channelTypes || []}
              />
            </Box>
            <Box display="flex" alignItems="center" sx={{ height: '56px' }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setShowFiltersModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minWidth: 160,
                  height: '56px',
                }}
              >
                Filtros Avanzados
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Modal de Filtros Avanzados */}
        <Dialog
          open={showFiltersModal}
          onClose={() => setShowFiltersModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <SearchIcon color="primary" />
              <Typography variant="h6">
                Filtros Avanzados
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Busca artículos específicos usando los filtros secundarios
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
            <TextField
              fullWidth
                  label="Código del artículo"
                  placeholder="SC0000010..."
                  value={quickSearch.code}
                  onChange={(e) => handleQuickSearchChange('code', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Descripción"
                  placeholder="iPhone 14..."
                  value={quickSearch.description}
                  onChange={(e) => handleQuickSearchChange('description', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  }}
            />
          </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Select
                    value={quickSearch.status || ''}
                    label="Estado del artículo"
                    onChange={(e) => handleQuickSearchChange('status', parseInt(e.target.value as string) || 0)}
                    displayEmpty
                  >
                    <MenuItem value="">Sin estado</MenuItem>
                    {productStates?.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
              fullWidth
                  label="Referencia"
                  placeholder="REF123..."
                  value={localFilters.reference}
                  onChange={(e) => handleReferenceChange(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
          </Grid>
        </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={() => {
                setQuickSearch({ code: '', description: '', status: 0 });
                handleClearFilters();
                setShowFiltersModal(false);
              }}
              startIcon={<ClearIcon />}
              sx={{ borderRadius: 2 }}
            >
              Limpiar Todo
            </Button>
            <Button
              onClick={() => setShowFiltersModal(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                handleQuickSearchSubmit();
                setShowFiltersModal(false);
              }}
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{ borderRadius: 2 }}
            >
              Aplicar Filtros
            </Button>
          </DialogActions>
        </Dialog>

        {hasActiveFilters && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Filtros activos:
              {localFilters.location_id && 
                ` | Ubicación: ${locations?.find(loc => loc.id === localFilters.location_id)?.code}`
              }
              {localFilters.container_id && 
                ` | Contenedor: ${containers?.find(cont => cont.id === localFilters.container_id)?.code}`
              }
              {quickSearch.code && ` | Código: "${quickSearch.code}"`}
              {quickSearch.description && ` | Descripción: "${quickSearch.description}"`}
              {quickSearch.status > 0 && 
                ` | Estado: "${productStates?.find(state => state.id === quickSearch.status)?.name || 'Desconocido'}"`
              }
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
